import { Link } from 'react-router-dom'
import type { ISODate, Task } from '@app/shared'
import clsx from 'clsx'

import type { MailTriage } from '../../lib/data/types'
import { reminderTime, sinceWhen } from './day'
import { Glyph } from './glyphs'

// ============================================================================
// BOARD 01 · THE MAIL CARD AND THE REMINDERS CARD, and the honest states both of them have
// (board 26): a skeleton of the card's own shape while loading, the read's failure in words with a
// Retry (never a blank card), the empty sentence, and an "as of" line when the phone is offline.
//
// THE MAIL CARD'S LAYOUT — Owner 2026-11-04: count in the header, subjects full width (board 01
// drew them beside the count; at 130% that cut subjects at ~12 characters).
// ============================================================================

/** Board 26: a failed read says so and offers the one thing that can help. */
function Failed({ what, error, onRetry, slab }: { what: string; error: unknown; onRetry: () => void; slab?: boolean }) {
  return (
    <div className="flex items-center gap-[10px]">
      <span className={clsx('min-w-0 flex-1 text-[13.5px] font-semibold leading-[1.4]', slab ? 'text-m-on-slab' : 'text-m-ink')}>
        {what} could not be read.{' '}
        <span className={slab ? 'text-m-on-slab-sub' : 'text-m-sub'}>{error instanceof Error ? error.message : ''}</span>
      </span>
      <button
        type="button"
        onClick={onRetry}
        className={clsx(
          'm-tap h-[44px] flex-none rounded-[16px] px-4 text-[14px] font-extrabold',
          slab ? 'bg-m-slab-wash text-m-on-slab' : 'bg-m-slab text-m-on-slab',
        )}
      >
        Retry
      </button>
    </div>
  )
}

/** A loading card: the card's own radius and height, pulsing. Never a spinner over content. */
export function CardSkeleton({ slab, height }: { slab?: boolean; height: number }) {
  return (
    <div
      aria-hidden
      className={clsx('flex-none animate-pulse rounded-[30px]', slab ? 'bg-m-slab' : 'bg-m-card')}
      style={{ height }}
    />
  )
}

// ── Mail ────────────────────────────────────────────────────────────────────────────────────

export function MailCard({
  today,
  triage,
  error,
  loading,
  asOf,
  onOpen,
  onRetry,
  onSyncGmail,
  syncingGmail,
}: {
  /** For the "as of" times: one from an earlier day carries that day, never a bare clock. */
  today: ISODate | undefined
  triage: MailTriage | undefined
  error: unknown
  loading: boolean
  /** Offline: when the mail was last synced, for the "as of" line. */
  asOf: string | null
  onOpen: () => void
  onRetry: () => void
  onSyncGmail: () => void
  syncingGmail: boolean
}) {
  if (loading || (!triage && !error)) return <CardSkeleton height={116} />

  const label = (
    <div className="flex items-center gap-[7px] text-[13px] font-bold text-m-sub">
      <Glyph name="mail" size={16} />
      Mail
    </div>
  )

  if (!triage) {
    return (
      <section className="flex min-h-[116px] flex-none flex-col justify-center gap-[10px] rounded-[30px] bg-m-card px-[18px] py-4 text-m-ink">
        {label}
        <Failed what="Mail" error={error} onRetry={onRetry} />
      </section>
    )
  }

  // NO GMAIL ACCOUNT: there is nothing to count, and connecting one happens on the desktop (board
  // 26's hand-off). The card says so and opens nothing.
  if (triage.accounts.length === 0) {
    return (
      <section className="flex min-h-[116px] flex-none items-center gap-4 rounded-[30px] bg-m-card px-[18px] py-4 text-m-ink">
        <div className="flex w-[92px] flex-none flex-col gap-[6px]">{label}</div>
        <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
          <span className="text-[15px] font-extrabold leading-[1.2]">Connect Gmail on the desktop</span>
          <span className="text-[12.5px] font-semibold text-m-sub">Settings › Connections. The phone picks it up on its next sync.</span>
        </div>
      </section>
    )
  }

  const count = triage.counts.needsYouToday
  const subjects = triage.groups.needsYouToday.filter((m) => m.state === 'open').slice(0, 3)
  const broken = triage.accounts.find((a) => a.lastError !== null)

  return (
    <section className="relative flex min-h-[116px] flex-none flex-col justify-center gap-[6px] rounded-[30px] bg-m-card px-[18px] py-[14px] text-m-ink">
      <button type="button" onClick={onOpen} aria-label="Open important mail" className="m-tap absolute inset-0 z-[1] rounded-[30px]" />
      {/* The header: "Mail" left, the count and "need you" right, on one baseline. */}
      <div className={clsx('mb-[2px] flex items-baseline justify-between gap-3', broken && 'opacity-60')}>
        {label}
        <div className="flex flex-none items-baseline gap-[6px]">
          <span className="text-[40px] font-extrabold leading-[0.9]">{count}</span>
          <span className="whitespace-nowrap text-[13px] font-semibold text-m-sub">need you</span>
        </div>
      </div>
      {/* The subjects: each its own full-width row, one line, under the header. */}
      {subjects.length === 0 ? (
        <span className="text-[14px] font-semibold text-m-sub">Nothing needs you</span>
      ) : (
        subjects.map((m, i) => (
          <div key={m.id} className="flex min-w-0 items-center gap-2">
            <span className={clsx('size-2 flex-none rounded-full', i === 0 ? 'bg-m-accent' : 'bg-m-ink')} aria-hidden />
            <span className={clsx('min-w-0 truncate text-[14px] leading-[1.3]', i === 0 ? 'font-bold' : 'font-semibold')}>
              {m.subject || '(no subject)'}
            </span>
          </div>
        ))
      )}
      {broken ? (
        <div className="relative z-[2] mt-[4px] flex items-center gap-[10px] border-t border-m-line pt-[10px]">
          <span className="min-w-0 flex-1 text-[13px] font-semibold leading-[1.4]">
            {broken.lastError}
            {broken.lastSyncAt ? ` The count is from ${sinceWhen(broken.lastSyncAt, today)}.` : ''}
          </span>
          <button
            type="button"
            onClick={onSyncGmail}
            disabled={syncingGmail}
            className="m-tap h-[44px] flex-none rounded-[16px] bg-m-slab px-4 text-[14px] font-extrabold text-m-on-slab disabled:opacity-60"
          >
            {syncingGmail ? 'Trying…' : 'Retry'}
          </button>
        </div>
      ) : (
        asOf && <span className="mt-[4px] text-[12.5px] font-semibold text-m-sub">Offline · as of {sinceWhen(asOf, today)}</span>
      )}
    </section>
  )
}

// ── Reminders ───────────────────────────────────────────────────────────────────────────────

export function RemindersCard({
  today,
  open,
  loading,
  error,
  asOf,
  onRetry,
  onComplete,
}: {
  today: ISODate
  open: Task[] | null
  loading: boolean
  error: unknown
  /** Offline: when this phone last read the reminders. */
  asOf: string | null
  onRetry: () => void
  onComplete: (task: Task) => void
}) {
  if (loading || (!open && !error)) return <CardSkeleton slab height={190} />

  const header = (count: number | null) => (
    <div className="mb-[6px] flex items-center justify-between">
      <div className="flex items-center gap-[7px] text-[13px] font-bold text-m-on-slab-sub">
        <Glyph name="bell" size={16} />
        Reminders
      </div>
      {count !== null && <span className="text-[13px] font-bold text-m-accent">{count} today</span>}
    </div>
  )

  if (!open) {
    return (
      <section className="flex flex-none flex-col gap-1 rounded-[30px] bg-m-slab px-[18px] pb-[14px] pt-[18px] text-m-on-slab">
        {header(null)}
        <Failed what="Reminders" error={error} onRetry={onRetry} slab />
      </section>
    )
  }

  const shown = open.slice(0, 3)
  const more = open.length - shown.length

  return (
    <section className="relative flex flex-none flex-col gap-1 rounded-[30px] bg-m-slab px-[18px] pb-[10px] pt-[18px] text-m-on-slab">
      <Link to="/tasks" aria-label="Open Reminders" className="absolute bottom-0 left-[60px] right-0 top-0 z-[1] rounded-[30px]" />
      {header(open.length)}
      {open.length === 0 && (
        <div className="flex min-h-[44px] items-center text-[16px] font-semibold text-m-on-slab-sub">Nothing due today</div>
      )}
      {shown.map((task, i) => {
        const time = reminderTime(task, today)
        const hot = i === 0 || time === 'overdue'
        return (
          <div key={task.id}>
            {i > 0 && <div className="h-px bg-m-slab-line" aria-hidden />}
            <div className="flex min-h-[44px] items-center gap-3">
              <button
                type="button"
                aria-label={`Mark done: ${task.title}`}
                onClick={() => onComplete(task)}
                className={clsx(
                  'm-tap-round relative z-[2] box-border size-6 flex-none rounded-full border-2',
                  hot ? 'border-m-accent' : 'border-m-on-slab-sub',
                )}
              />
              <span className="min-w-0 flex-1 truncate text-[16px] font-semibold">{task.title}</span>
              {time && (
                <span className={clsx('flex-none text-[13px] font-semibold', hot ? 'text-m-accent' : 'text-m-on-slab-sub')}>
                  {time}
                </span>
              )}
            </div>
          </div>
        )
      })}
      {more > 0 && (
        <>
          <div className="h-px bg-m-slab-line" aria-hidden />
          <div className="flex min-h-[44px] items-center pl-9 text-[14px] font-bold text-m-on-slab-sub">+{more} more</div>
        </>
      )}
      {asOf && <span className="pb-[6px] text-[12.5px] font-semibold text-m-on-slab-sub">Offline · as of {sinceWhen(asOf, today)}</span>}
    </section>
  )
}
